/**
 * The Digital3DView public surface — the LIGHT half.
 *
 * Nothing exported from here pulls three.js into the importer's bundle. That is
 * the whole point of splitting this from `scene.js`: `MachineDetails.js` and
 * `DigitalTwinWidget.js` need only a WebGL capability probe and a reducer, and
 * routing those through a barrel that also re-exported SceneCanvas would drag
 * ~141 KB gzipped of three.js back onto the default machine tab and quietly
 * undo the code-splitting done in DT-310.
 *
 * So the boundary is drawn by weight, not by folder:
 *
 *   import { getWebGLSupportTier } from '.../Digital3DView';        // light
 *   import { SceneCanvas } from '.../Digital3DView/scene';          // heavy
 *
 * Consumers already behind a React.lazy boundary (EmbeddedDigitalTwinScene)
 * may import from either. Everything else must stay on this one.
 *
 * `modelConventions.test.js` and the production-build chunk check are what keep
 * that honest — if something heavy leaks in here, three.js reappears in the
 * MachineOverview chunk group.
 *
 * Everything re-exported here is dependency-light and, crucially, none of it
 * reaches `common/utils`. The action creators do, and `common/utils` comes back
 * around through the app store to `MachineDetails/index.js` — so re-exporting
 * them from here made this barrel unimportable from any leaf module ("Cannot
 * access 'getCurrentAccount' before initialization"). They live in `./store`
 * for that reason; keep them out of this file.
 */

export { default as digitalTwinReducer } from './reducers/digitalTwin.reducer';
export { initialState as digitalTwinInitialState } from './reducers/digitalTwin.reducer';

export { digitalTwinActionTypes } from './actions/digitalTwin.types';

export {
  selectDigitalTwinScene,
  selectDigitalTwinState,
  selectDigitalTwinSummary,
  selectDigitalTwinEditState,
} from './selectors/digitalTwin.selectors';

export { default as useWebGLSupport, getWebGLSupportTier } from './hooks/useWebGLSupport';
export { default as useActivityEvents } from './hooks/useActivityEvents';
export { default as useSceneEventHighlights } from './hooks/useSceneEventHighlights';

export {
  walkInfoLocationsForComponent,
  walkAllComponentLocations,
  collectMountedSensorsByLocation,
  findInfoComponent,
  relativeTime,
} from './utils/readings';

export { getSceneManagerFromRef } from './utils/sceneManagerRef';
export { formatTitleText, findComponentNode, formatMeasurementStrategy } from './utils/twinComponentModel';
export { formatPartLabel } from './utils/partLabels';

export { resolveRegistryKey, getModelFootprint, hasRealModel } from './models/registry';

export { EDIT_MODES } from './constants/editModes';
